import React, { useState, useEffect, useRef } from "react";
import { findNodeHandle } from "react-native";
import { View, Text, Image, TextInput, Pressable, FlatList, StyleSheet, Dimensions, ImageBackground, Modal, Alert } from "react-native";
import { Ionicons } from '@expo/vector-icons';
import { getFirestore, doc, updateDoc, arrayUnion, getDocs, query, collection, where, arrayRemove } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useUser } from "../../Widgets/UserContext";
import exampleBackground1 from "../../assets/exBackground1.jpg";
import exampleBackground2 from "../../assets/exBackground2.jpg";
import exampleBackground3 from "../../assets/exBackground3.jpg";
import exampleBackground4 from "../../assets/exBackground4.jpg";
import exampleBackground5 from "../../assets/exBackground5.jpg";
import exampleBackground6 from "../../assets/exBackground6.jpg";
import exampleBackground7 from "../../assets/exBackground7.jpg";
import genericProfilePic from "../../assets/generic.jpg";
import logo from "../../assets/logo.png";

const { width } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width <= 1500;

const fontFamilies = { Bodoni72Smallcaps: "Bodoni72Smallcaps" };

const defaultBackgrounds = [
  exampleBackground1,
  exampleBackground2,
  exampleBackground3,
  exampleBackground4,
  exampleBackground5,
  exampleBackground6,
  exampleBackground7,
];

const Yearbook = ({ pageOwnerId, pageOwner, targetHeight, onHeightChange }) => {
  const db = getFirestore();
  const storage = getStorage();
  const { currentUserData } = useUser();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [signature, setSignature] = useState({ firstName: "", lastName: "" });
  const [profilePictures, setProfilePictures] = useState({});
  const [lastThreeSenders, setLastThreeSenders] = useState([]);
  const [pageOwnerEmail, setPageOwnerEmail] = useState("");
  const flatListRef = useRef();
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [image, setImage] = useState(null);

  const [sessionId, setSessionId] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionsFor, setShowActionsFor] = useState(null);
  const [actionsMenuData, setActionsMenuData] = useState(null);
  const [actionsMenuPosition, setActionsMenuPosition] = useState(null);
  const messageRefs = useRef({});

  const [showReplyModal, setShowReplyModal] = useState(false);
  const [messageToReply, setMessageToReply] = useState(null);
  const [replyText, setReplyText] = useState("");

  const FIREBASE_SERVER_URL = "https://us-central1-my-tassel.cloudfunctions.net/api";
  const SERVER_URL = FIREBASE_SERVER_URL;

  const generateSessionId = () => 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

  const getOrCreateSessionId = () => {
    try {
      let stored = localStorage.getItem('yearbookSessionId');
      if (!stored) { stored = generateSessionId(); localStorage.setItem('yearbookSessionId', stored); }
      return stored;
    } catch (e) {
      return generateSessionId();
    }
  };

  const generateMessageId = () => 'msg-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

  const canEditMessage = (messageData) => {
    if (currentUserData?.uid === pageOwnerId) return true;
    if (currentUserData?.uid && (messageData.senderUID === currentUserData.uid || messageData.createdBy === currentUserData.uid)) return true;
    if (!currentUserData?.uid && messageData.sessionId === sessionId && messageData.senderUID === 'anonymous') return true;
    return false;
  };

  const isPageOwner = () => currentUserData?.uid === pageOwnerId;

  useEffect(() => {
    const sid = getOrCreateSessionId();
    setSessionId(sid);
    loadMessages();
  }, [pageOwnerId]);

  useEffect(() => {
    if (pageOwner) {
      if (pageOwner.gradCardBackgroundImage) setBackgroundImage(pageOwner.gradCardBackgroundImage);
      else if (pageOwner.gradCardBackgroundIndex !== undefined) setBackgroundImage(defaultBackgrounds[pageOwner.gradCardBackgroundIndex]);
      else setBackgroundImage(exampleBackground2);
    }
  }, [pageOwner]);

  useEffect(() => {
    if (flatListRef.current && messages.length > 0) {
      setTimeout(() => {
        try { flatListRef.current.scrollToEnd({ animated: true }); } catch (e) {}
      }, 120);
    }
  }, [messages]);

  const loadMessages = async () => {
    try {
      const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        querySnapshot.forEach(async (docSnapshot) => {
          const data = docSnapshot.data();
          setPageOwnerEmail(data.email || "");
          const rawMessages = data.messages?.yearbookMessages || [];
          let updated = false;
          const messagesWithIds = rawMessages.map(msg => {
            if (!msg.id) { updated = true; return { ...msg, id: generateMessageId(), createdBy: msg.createdBy || msg.senderUID || 'unknown' }; }
            return { ...msg, createdBy: msg.createdBy || msg.senderUID || 'unknown' };
          });
          if (updated) {
            try { await updateDoc(doc(db, "users", docSnapshot.id), { "messages.yearbookMessages": messagesWithIds }); } catch (e) {}
          }
          const getTime = (timestamp) => {
            if (!timestamp) return 0;
            if (timestamp instanceof Date) return timestamp.getTime();
            if (typeof timestamp.toDate === 'function') return timestamp.toDate().getTime();
            if (typeof timestamp === 'number') return timestamp;
            if (typeof timestamp === 'string') return new Date(timestamp).getTime();
            if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000;
            return 0;
          };
          const sorted = messagesWithIds.slice().sort((a, b) => getTime(a.timestamp) - getTime(b.timestamp));
          setMessages(sorted);

          // Pre-fetch profile pictures for all senders to avoid missing/generic avatars
          try {
            const senderUIDs = Array.from(new Set(sorted.map(m => m.senderUID).filter(u => u && u !== 'anonymous')));
            senderUIDs.forEach(uid => { fetchProfilePictureUrl(uid); });
          } catch (e) {
            // ignore
          }
        });
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const fetchProfilePictureUrl = async (uid) => {
    if (!uid || profilePictures[uid]) return;
    try {
      const numericPartOfUID = uid.substring(1);
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("uid", "in", [`F${numericPartOfUID}`, `G${numericPartOfUID}`, `A${numericPartOfUID}`]));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.uid && (data.uid.startsWith('F') || data.uid.startsWith('G') || data.uid.startsWith('A'))) {
            setProfilePictures(prev => ({ ...prev, [uid]: data.profilePictureURL }));
          }
        });
      }
    } catch (error) {}
  };

  const isMessageFromPageOwner = (msg) => msg && (msg.senderUID === pageOwnerId || msg.createdBy === pageOwnerId);

  // Whether the message sender is the current viewer (the person holding the phone).
  // This should be true for logged-in users whose uid matches the sender, or for anonymous
  // visitors when the message.sessionId matches the current session.
  const isSenderCurrentViewer = (msg) => {
    if (!msg) return false;
    if (currentUserData?.uid) return msg.senderUID === currentUserData.uid || msg.createdBy === currentUserData.uid;
    return msg.sessionId === sessionId && msg.senderUID === 'anonymous';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      let d;
      if (timestamp instanceof Date) d = timestamp;
      else if (typeof timestamp.toDate === 'function') d = timestamp.toDate();
      else d = new Date(timestamp);
      return d.toLocaleString();
    } catch (e) { return '' }
  };

  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (file && file.type && file.type.startsWith('image')) {
      const storageRef = ref(storage, `yearbookImages/${file.name}-${Date.now()}`);
      const uploadResult = await uploadBytes(storageRef, file);
      const publicUrl = await getDownloadURL(uploadResult.ref);
      setImage(publicUrl);
    }
  };

  const handleEditImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (file && file.type && file.type.startsWith('image')) {
      const storageRef = ref(storage, `yearbookImages/${file.name}-${Date.now()}`);
      const uploadResult = await uploadBytes(storageRef, file);
      const publicUrl = await getDownloadURL(uploadResult.ref);
      setEditImage(publicUrl);
    }
  };

  const handleSend = async (overrideText, replyingTo) => {
    const textToSend = overrideText !== undefined ? overrideText : message;
    if (!textToSend || textToSend.trim() === '') return;
    const newMsg = {
      id: generateMessageId(),
      text: textToSend,
      image: image || null,
      senderUID: currentUserData?.uid || 'anonymous',
      senderFirstName: currentUserData?.firstName || signature.firstName || 'Anonymous',
      senderLastName: currentUserData?.lastName || signature.lastName || '',
      sessionId: sessionId,
      timestamp: Date.now(),
      createdBy: currentUserData?.uid || 'anonymous',
    };
    if (replyingTo) {
      newMsg.isReply = true;
      newMsg.replyToId = replyingTo.id || null;
      newMsg.originalSenderName = `${replyingTo.senderFirstName || ''} ${replyingTo.senderLastName || ''}`.trim();
      newMsg.originalMessageText = replyingTo.text || replyingTo.message || '';
    }
    try {
      const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
      const snap = await getDocs(q);
      if (!snap.empty) {
        snap.forEach(async (docSnap) => {
          await updateDoc(doc(db, "users", docSnap.id), { "messages.yearbookMessages": arrayUnion(newMsg) });
        });
      }
      setMessages(prev => [...prev, newMsg]);
      setMessage('');
      setImage(null);
    } catch (e) {
      console.error('Error sending message', e);
    }
  };

  const handleEditMessage = (msg) => {
    setEditingMessage(msg);
    setEditText(msg.text || '');
    setEditImage(msg.image || null);
    setShowEditModal(true);
  };

  const handleUpdateMessage = async () => {
    if (!editingMessage) return;
    try {
      const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
      const snap = await getDocs(q);
      if (!snap.empty) {
        snap.forEach(async (docSnap) => {
          const data = docSnap.data();
          const raw = data.messages?.yearbookMessages || [];
          const updated = raw.map(m => m.id === editingMessage.id ? { ...m, text: editText, image: editImage, lastEditedAt: Date.now() } : m);
          await updateDoc(doc(db, "users", docSnap.id), { "messages.yearbookMessages": updated });
          setMessages(updated);
        });
      }
    } catch (e) {
      console.error('Error updating message', e);
    }
    setShowEditModal(false);
    setEditingMessage(null);
  };

  const handleHideMessage = async (msg) => {
    try {
      const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
      const snap = await getDocs(q);
      if (!snap.empty) snap.forEach(async (docSnap) => {
        const data = docSnap.data();
        const raw = data.messages?.yearbookMessages || [];
        const updated = raw.map(m => m.id === msg.id ? { ...m, hidden: !m.hidden } : m);
        await updateDoc(doc(db, "users", docSnap.id), { "messages.yearbookMessages": updated });
        setMessages(updated);
      });
    } catch (e) {
      console.error('Error toggling hide', e);
    }
  };

  const handleDeleteMessage = async (msg) => {
    const doDelete = async () => {
      try {
        const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
        const snap = await getDocs(q);
        if (!snap.empty) snap.forEach(async (docSnap) => {
          const data = docSnap.data();
          const raw = data.messages?.yearbookMessages || [];
          const updated = raw.filter(m => m.id !== msg.id);
          await updateDoc(doc(db, "users", docSnap.id), { "messages.yearbookMessages": updated });
          setMessages(updated);
        });
      } catch (e) { console.error('Error deleting message', e); }
    };

    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      if (window.confirm('Delete message? Are you sure?')) {
        await doDelete();
      }
    } else {
      Alert.alert('Delete message', 'Are you sure?', [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: async () => { await doDelete(); } }
      ]);
    }
  };

  const handleReplyClick = (msg) => { setMessageToReply(msg); setReplyText(''); setShowReplyModal(true); };

  const handleSendReply = () => {
    if (replyText.trim()) { handleSend(replyText, messageToReply); setShowReplyModal(false); setMessageToReply(null); setReplyText(''); }
  };

  // Group messages into threads for iMessage-style reply nesting
  const threadedMessages = React.useMemo(() => {
    const parents = messages.filter(m => !m.isReply);
    const replyMap = {};
    messages.filter(m => m.isReply).forEach(reply => {
      let parentKey = '__orphan';
      for (let i = 0; i < parents.length; i++) {
        const p = parents[i];
        if (reply.replyToId && p.id && reply.replyToId === p.id) {
          parentKey = p.id || i;
          break;
        }
        const pText = (p.text || p.message || '').trim();
        const rText = (reply.originalMessageText || '').trim();
        if (rText && pText && pText === rText) {
          parentKey = p.id || i;
          break;
        }
      }
      if (!replyMap[parentKey]) replyMap[parentKey] = [];
      replyMap[parentKey].push(reply);
    });
    const orphans = replyMap['__orphan'] || [];
    // Build thread items: parent followed by its replies
    const result = [];
    parents.forEach((p, idx) => {
      result.push({ ...p, _isThread: false });
      const replies = (replyMap[p.id || idx] || []).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
      replies.forEach(r => result.push({ ...r, _isThread: true }));
    });
    orphans.forEach(o => result.push({ ...o, _isThread: true }));
    return result;
  }, [messages]);

  const renderSingleBubble = (item, isReplyMsg = false) => {
    const isSenderViewer = isSenderCurrentViewer(item);
    const shouldHidePictureForViewer = isSenderViewer;
    const profilePicUrl = shouldHidePictureForViewer ? null : (item.senderPhotoURL || profilePictures[item.senderUID] || genericProfilePic);
    const canEdit = canEditMessage(item);
    const isHidden = item.hidden && !(isPageOwner() || isSenderViewer);
    const isHiddenForOwner = item.hidden && isPageOwner();
    const alignRight = isSenderViewer;
    if (isHidden) return null;
    return (
      <View ref={ref => { if (ref && item.id) messageRefs.current[item.id] = ref; }}>
        {isReplyMsg && (
          <View style={[{
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 1,
            marginTop: 2,
          }, alignRight ? { justifyContent: 'flex-end', marginRight: 10 } : { marginLeft: 40 }]}>
            {/* Curved reply connector line */}
            <View style={{
              width: 20,
              height: 24,
              marginRight: alignRight ? 0 : 4,
              marginLeft: alignRight ? 4 : 0,
              order: alignRight ? 1 : 0,
            }}>
              <div style={{
                width: 20,
                height: 24,
                borderLeft: alignRight ? 'none' : '2px solid rgba(76,149,108,0.35)',
                borderRight: alignRight ? '2px solid rgba(76,149,108,0.35)' : 'none',
                borderTop: '2px solid rgba(76,149,108,0.35)',
                borderTopLeftRadius: alignRight ? 0 : 12,
                borderTopRightRadius: alignRight ? 12 : 0,
              }} />
            </View>
            <Text style={{
              fontSize: 11,
              color: '#4c956c',
              fontStyle: 'italic',
              fontFamily: 'Orpheus Pro',
              order: alignRight ? 0 : 1,
            }}>
              {item.originalSenderName ? `Replying to ${item.originalSenderName}` : 'Reply'}
            </Text>
          </View>
        )}
        <View style={[styles.messageCard, alignRight ? styles.alignRight : styles.alignLeft, isHiddenForOwner && styles.hiddenMessageContainer, isReplyMsg && { marginLeft: alignRight ? 0 : 28, marginBottom: 4 }, { position: 'relative' }]}>
          {profilePicUrl ? (
            <Image source={{ uri: profilePicUrl }} style={[styles.profileImage, isHiddenForOwner && styles.hiddenImage]} />
          ) : null}
          <View style={[styles.messageContent, isHiddenForOwner && styles.hiddenMessage]}>
            <View style={styles.messageHeader}>
              <Text style={[alignRight ? styles.messageSender1 : styles.messageSender2, isHiddenForOwner && styles.hiddenText]}>{shouldHidePictureForViewer ? 'You' : `${item.senderFirstName || ''} ${item.senderLastName || ''}`}{isHiddenForOwner ? ' (Hidden)' : ''}</Text>
              <View style={styles.messageActions}>
                {canEdit && (
                  <Pressable onPress={() => openActionsMenu(item)}>
                    <Ionicons name="ellipsis-horizontal" size={14} color="#333" />
                  </Pressable>
                )}
              </View>
            </View>
            {item.image && <Image source={{ uri: item.image }} style={[styles.messageImage, isHiddenForOwner && styles.hiddenImage]} />}
            <Text style={[alignRight ? styles.messageText1 : styles.messageText2, isHiddenForOwner && styles.hiddenText]}>{item.text}</Text>
            <View style={styles.timestampContainer}>
              <Text style={alignRight ? styles.messageTimestamp1 : styles.messageTimestamp2}>{formatDate(item.timestamp)}</Text>
              {item.lastEditedAt && (<Text style={alignRight ? styles.editedTimestamp1 : styles.editedTimestamp2}>(edited)</Text>)}
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderItem = ({ item }) => {
    if (!item) return null;
    if (item.isCongratulatory) {
      return (
        <View style={[styles.messageCard, styles.alignLeft]}>
          <Image source={logo} style={styles.profileImage} />
          <View style={styles.messageContent}>
            <Text style={styles.messageSender2}>The MyTassel Team</Text>
            <Text style={styles.messageText2}>Congratulations on your accomplishment! Your hard work and perseverance paid off! As you share your page, Friends and Family can use this space to share messages, memories, and upload their favorite photos to celebrate YOU!</Text>
          </View>
        </View>
      );
    }
    return renderSingleBubble(item, item._isThread || false);
  };

  const openActionsMenu = (item) => {
    const ref = messageRefs.current[item.id];
    const setPos = (pageX, pageY, w, h) => {
      setActionsMenuPosition({ x: pageX, y: pageY, width: w, height: h });
      setActionsMenuData(item);
      setShowActionsFor(item.id);
    };
    try {
      if (ref) {
        if (typeof ref.measureInWindow === 'function') {
          ref.measureInWindow((pageX, pageY, w, h) => setPos(pageX, pageY, w, h));
        } else if (typeof ref.measure === 'function') {
          ref.measure((x, y, w, h, pageX, pageY) => setPos(pageX, pageY, w, h));
        } else {
          setActionsMenuData(item);
          setShowActionsFor(item.id);
        }
      } else {
        setActionsMenuData(item);
        setShowActionsFor(item.id);
      }
    } catch (e) {
      setActionsMenuData(item);
      setShowActionsFor(item.id);
    }
  };

  return (
    <View style={styles.scrollView}>
      <View
        style={styles.container(targetHeight)}
        onLayout={(e) => {
          const { width } = Dimensions.get('window');
          if (width >= 1024 && onHeightChange) onHeightChange(e.nativeEvent.layout.height);
        }}
      >
        <ImageBackground source={{ uri: backgroundImage }} imageStyle={styles.yearbookImage} style={[styles.yearbookContainer(targetHeight), { flex: 1, height: '100%' }]}> 
          <View style={{ flex: 1, backgroundColor: "rgba(255, 255, 255, 0.8)", borderRadius: 20, marginBottom: 10, paddingHorizontal: 20, paddingTop: 10 }}>
            <FlatList
              ref={flatListRef}
              style={{ flex: 1, width: '100%', ...(isMobile ? { maxHeight: 300 } : {}) }}
              contentContainerStyle={{ flexGrow: 1, justifyContent: 'flex-end' }}
              nestedScrollEnabled={true}
              data={pageOwnerId ? [{ isCongratulatory: true }, ...threadedMessages] : threadedMessages}
              keyExtractor={(item, index) => item?.id ? item.id : "message-" + index}
              showsVerticalScrollIndicator={false}
              renderItem={({ item }) => renderItem({ item })}
            />
          </View>
          <View style={styles.yearbookInputContainer}>
            <TextInput style={[styles.yearbookInput, { marginTop: 10 }]} placeholder="Write a message..." value={message} onChangeText={setMessage} />
            {!currentUserData?.uid && (
              <>
                <TextInput style={styles.input} placeholder="First Name" value={signature.firstName} onChangeText={(text) => setSignature({ ...signature, firstName: text })} />
                <TextInput style={styles.input} placeholder="Last Name" value={signature.lastName} onChangeText={(text) => setSignature({ ...signature, lastName: text })} />
              </>
            )}
          </View>
          <View style={styles.buttonContainer}>
            <Pressable style={styles.uploadButton} onPress={() => document.getElementById('fileInput').click()}><Text style={styles.uploadButtonText}>Upload Photo</Text></Pressable>
            <input id="fileInput" type="file" onChange={handleImageUpload} accept="image/*" style={{ display: 'none' }} />
            <Pressable style={styles.sendButton} onPress={() => handleSend()}><Text style={styles.sendButtonText}>Send</Text></Pressable>
          </View>
          {image && (<View style={styles.previewContainer}><Image source={{ uri: image }} style={styles.previewImage} /></View>)}
        </ImageBackground>
      </View>

      <Modal visible={showActionsFor !== null && actionsMenuData !== null} transparent animationType="none" onRequestClose={() => { setShowActionsFor(null); setActionsMenuData(null); setActionsMenuPosition(null); }}>
        <Pressable style={styles.actionsModalOverlay} onPress={() => { setShowActionsFor(null); setActionsMenuData(null); setActionsMenuPosition(null); }}>
          <View style={[styles.actionsMenu, actionsMenuPosition && { position: 'absolute', left: actionsMenuPosition.x + (actionsMenuPosition.width || 0) + 8, top: actionsMenuPosition.y }]} onStartShouldSetResponder={() => true}>
            {actionsMenuData && (
              <>
                {currentUserData?.uid && (
                  <Pressable style={styles.menuButton} onPress={() => { handleReplyClick(actionsMenuData); setShowActionsFor(null); setActionsMenuData(null); setActionsMenuPosition(null); }}>
                    <Ionicons name="return-up-forward" size={14} color="#666" />
                    <Text style={styles.menuButtonText}>Reply</Text>
                  </Pressable>
                )}
                {(currentUserData?.uid === actionsMenuData?.senderUID || currentUserData?.uid === actionsMenuData?.createdBy || (!currentUserData?.uid && actionsMenuData?.sessionId === sessionId && actionsMenuData?.senderUID === 'anonymous')) && (
                  <Pressable style={styles.menuButton} onPress={() => { handleEditMessage(actionsMenuData); setShowActionsFor(null); setActionsMenuData(null); setActionsMenuPosition(null); }}>
                    <Ionicons name="create-outline" size={14} color="#666" />
                    <Text style={styles.menuButtonText}>Edit</Text>
                  </Pressable>
                )}
                {isPageOwner() && actionsMenuData?.senderUID !== currentUserData?.uid ? (
                  <>
                    <Pressable style={styles.menuButton} onPress={() => { handleHideMessage(actionsMenuData); setShowActionsFor(null); setActionsMenuData(null); setActionsMenuPosition(null); }}>
                      <Ionicons name={actionsMenuData?.hidden ? 'eye' : 'eye-off'} size={14} color="#666" />
                      <Text style={styles.menuButtonText}>{actionsMenuData?.hidden ? 'Unhide' : 'Hide'}</Text>
                    </Pressable>
                    <Pressable style={styles.menuButton} onPress={() => { handleDeleteMessage(actionsMenuData); setShowActionsFor(null); setActionsMenuData(null); setActionsMenuPosition(null); }}>
                      <Ionicons name="trash" size={14} color="#666" />
                      <Text style={styles.menuButtonText}>Delete</Text>
                    </Pressable>
                  </>
                ) : (
                  <>
                    {(currentUserData?.uid === actionsMenuData?.senderUID || currentUserData?.uid === actionsMenuData?.createdBy || (!currentUserData?.uid && actionsMenuData?.sessionId === sessionId && actionsMenuData?.senderUID === 'anonymous')) && (
                      <Pressable style={styles.menuButton} onPress={() => { handleDeleteMessage(actionsMenuData); setShowActionsFor(null); setActionsMenuData(null); setActionsMenuPosition(null); }}>
                        <Ionicons name="trash" size={14} color="#666" />
                        <Text style={styles.menuButtonText}>Delete</Text>
                      </Pressable>
                    )}
                  </>
                )}
              </>
            )}
          </View>
        </Pressable>
      </Modal>

      <Modal visible={showReplyModal} transparent animationType="fade" onRequestClose={() => setShowReplyModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Reply to Message</Text>
              <Pressable style={styles.modalCloseButton} onPress={() => setShowReplyModal(false)}><Text style={styles.modalCloseText}>✕</Text></Pressable>
            </View>
            {messageToReply && (<View style={styles.replyContext}><Text style={styles.replyContextText}>Replying to {messageToReply.senderFirstName} {messageToReply.senderLastName}: {messageToReply.text || messageToReply.message}</Text></View>)}
            <TextInput style={styles.editInput} placeholder="Write your reply..." value={replyText} onChangeText={setReplyText} multiline />
            <View style={styles.modalActionButtons}>
              <Pressable style={styles.cancelButton} onPress={() => setShowReplyModal(false)}><Text style={styles.cancelButtonText}>Cancel</Text></Pressable>
              <Pressable style={styles.updateButton} onPress={handleSendReply}><Text style={styles.updateButtonText}>Send Reply</Text></Pressable>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={showEditModal} transparent animationType="fade" onRequestClose={() => setShowEditModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Message</Text>
              <Pressable style={styles.modalCloseButton} onPress={() => setShowEditModal(false)}><Text style={styles.modalCloseText}>✕</Text></Pressable>
            </View>
            <TextInput style={styles.editInput} placeholder="Edit your message..." value={editText} onChangeText={setEditText} multiline />
            {editImage && (<View style={styles.editImageContainer}><Image source={{ uri: editImage }} style={styles.editImagePreview} /><Pressable style={styles.removeImageButton} onPress={() => setEditImage(null)}><Text style={styles.removeImageText}>Remove Image</Text></Pressable></View>)}
            <View style={styles.modalButtonContainer}>
              <Pressable style={styles.editUploadButton} onPress={() => document.getElementById('editFileInput').click()}><Text style={styles.editUploadButtonText}>Change Photo</Text></Pressable>
              <input id="editFileInput" type="file" onChange={handleEditImageUpload} accept="image/*" style={{ display: 'none' }} />
              <View style={styles.modalActionButtons}>
                <Pressable style={styles.cancelButton} onPress={() => setShowEditModal(false)}><Text style={styles.cancelButtonText}>Cancel</Text></Pressable>
                <Pressable style={styles.updateButton} onPress={handleUpdateMessage}><Text style={styles.updateButtonText}>Update</Text></Pressable>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: (targetHeight) => ({
    flex: 1,
    padding: isMobile ? 8 : isTablet ? 12 : isSmallDesktop ? 15 : 20,
    justifyContent: "flex-start",
    alignItems: "center",
    width: "100%",
    height: (() => { if (isMobile || isTablet) return "100%"; return targetHeight ? `${targetHeight}px` : "auto"; })(),
    minHeight: (() => { if (isMobile || isTablet) return undefined; return targetHeight ? `${targetHeight}px` : "650px"; })(),
    minWidth: isMobile ? undefined : 450,
    maxWidth: "100%",
    maxHeight: (() => { if (isMobile || isTablet) return undefined; return targetHeight ? `${targetHeight}px` : "650px"; })(),
  }),
  yearbookContainer: (targetHeight) => ({
    flex: 1,
    backgroundColor: "#fefde2",
    padding: isMobile ? 8 : isTablet ? 12 : 20,
    borderRadius: 25,
    width: isMobile ? '100%' : isTablet ? '90%' : 400,
    height: (() => { if (isMobile) return 650; if (isTablet) return 650; return targetHeight ? `${targetHeight}px` : '100%'; })(),
    maxWidth: isMobile ? '100%' : isTablet ? '90%' : 400,
    maxHeight: isMobile ? 500 : isTablet ? 650 : (targetHeight ? `${targetHeight}px` : 650),
    minHeight: isMobile ? 500 : isTablet ? 650 : (targetHeight ? `${targetHeight}px` : 650),
    alignSelf: 'center',
    marginTop: 0,
    marginBottom: 0,
    overflow: "hidden",
  }),
  profileImage: { width: 25, height: 25, borderRadius: 25, marginRight: 10 },
  messageCard: { padding: 10, borderRadius: 10, marginVertical: 5, flexDirection: "row", alignItems: "center" },
  messageContent: { flex: 1 },
  messageHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 },
  messageActions: { flexDirection: 'row', alignItems: 'center', position: 'relative' },
  actionsMenu: { position: 'absolute', flexDirection: 'column', padding: 8, backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#ddd', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 5, minWidth: 100, zIndex: 10000 },
  actionsModalOverlay: { flex: 1, backgroundColor: 'transparent' },
  menuButton: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 4, margin: 2, backgroundColor: '#fff', borderRadius: 6, borderWidth: 1, borderColor: '#eee', cursor: 'pointer' },
  menuButtonText: { fontSize: 12, color: '#333', marginLeft: 4 },
  timestampContainer: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  editedTimestamp1: { fontSize: 7, color: '#bbb', fontStyle: 'italic' },
  editedTimestamp2: { fontSize: 7, color: '#666', fontStyle: 'italic' },
  messageSender1: { fontWeight: "bold", marginBottom: 5, color: "#fff" },
  messageSender2: { fontWeight: "bold", marginBottom: 5, color: "#000" },
  messageText1: { fontSize: 16, color: "#fff" },
  messageText2: { fontSize: 16, color: "#000" },
  messageTimestamp1: { fontSize: 8, color: "#ddd", marginTop: 2 },
  messageTimestamp2: { fontSize: 8, color: "#000", marginTop: 2 },
  input: { borderWidth: 1, borderColor: "#ddd", padding: 10, fontSize: 18, borderRadius: 10, marginVertical: 10, backgroundColor: "#fff" },
  uploadButton: { backgroundColor: '#4c956c', alignItems: 'center', justifyContent: 'center', borderRadius: 40, paddingVertical: 10, paddingHorizontal: 20, marginRight: 10 },
  uploadButtonText: { fontFamily: 'Orpheus Pro', fontSize: isMobile ? 16 : 18, textAlign: 'center', color: '#e4e4e4' },
  sendButton: { backgroundColor: '#4c956c', alignItems: 'center', justifyContent: 'center', borderRadius: 40, paddingVertical: 10, paddingHorizontal: 20 },
  sendButtonText: { fontFamily: 'Orpheus Pro', fontSize: isMobile ? 16 : 18, textAlign: 'center', color: '#e4e4e4' },
  previewContainer: { marginTop: 10, alignItems: 'center' },
  previewImage: { width: 100, height: 100, borderRadius: 10 },
  messageImage: { width: 100, height: 100, borderRadius: 10, marginBottom: 5 },
  yearbookImage: { width: "100%", height: "100%", resizeMode: "cover" },
  scrollView: { flex: 1 },
  yearbookInputContainer: { flexDirection: 'column', alignItems: 'center', marginBottom: 10 },
  yearbookInput: { flex: 1, borderWidth: 1, borderColor: '#ddd', padding: 10, fontSize: 18, borderRadius: 10, backgroundColor: '#fff', width: '100%' },
  replyReference: { padding: 8, borderRadius: 8, marginBottom: 5, backgroundColor: '#e0e0e0', maxWidth: '70%' },
  replyReferenceLeft: { marginLeft: 35, alignSelf: 'flex-start' },
  replyReferenceRight: { marginRight: 35, alignSelf: 'flex-end' },
  replyReferenceText: { fontSize: 12, fontWeight: 'bold', color: '#555' },
  replyOriginalText: { fontSize: 12, color: '#777', marginTop: 2 },
  replyMessage: { borderLeftWidth: 3, borderLeftColor: '#4c956c', paddingLeft: 8 },
  alignRight: { alignSelf: "flex-end", backgroundColor: "#4c956c" },
  alignLeft: { alignSelf: "flex-start", backgroundColor: "#fefde2" },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  modalContent: { backgroundColor: '#fff', borderRadius: 15, padding: 20, width: isMobile ? '90%' : isTablet ? '70%' : '50%', maxWidth: 500 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  modalCloseButton: { padding: 5 },
  modalCloseText: { fontSize: 18, color: '#666' },
  editInput: { borderWidth: 1, borderColor: '#ddd', padding: 12, fontSize: 16, borderRadius: 8, marginBottom: 15, minHeight: 80, textAlignVertical: 'top', backgroundColor: '#f9f9f9' },
  editImageContainer: { marginBottom: 15, alignItems: 'center' },
  editImagePreview: { width: 120, height: 120, borderRadius: 8, marginBottom: 10 },
  removeImageButton: { backgroundColor: '#dc3545', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6 },
  removeImageText: { color: '#fff', fontSize: 12 },
  buttonContainer: { gap: 10 },
  modalButtonContainer: { gap: 15 },
  editUploadButton: { backgroundColor: '#6c757d', alignItems: 'center', justifyContent: 'center', borderRadius: 8, paddingVertical: 10, paddingHorizontal: 15 },
  editUploadButtonText: { color: '#fff', fontSize: 14, fontWeight: '500' },
  modalActionButtons: { flexDirection: 'row', justifyContent: 'space-between', gap: 10 },
  cancelButton: { flex: 1, backgroundColor: '#6c757d', alignItems: 'center', justifyContent: 'center', borderRadius: 8, paddingVertical: 12 },
  cancelButtonText: { color: '#fff', fontSize: 16, fontWeight: '500' },
  updateButton: { flex: 1, backgroundColor: '#4c956c', alignItems: 'center', justifyContent: 'center', borderRadius: 8, paddingVertical: 12 },
  updateButtonText: { color: '#fff', fontSize: 16, fontWeight: '500' },
  removeImageText: { color: '#fff' },
});

export default Yearbook;
